import React from 'react'
import { Link } from 'react-router-dom'
import { format } from 'date-fns'
import { ArrowLeft, Calendar, Package, Sparkles } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { buttonVariants } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Separator } from '@/components/ui/separator'
import { cn } from '@/lib/utils'
import type { ReleaseNote } from '@/lib/release-notes'

interface ReleaseNoteDetailProps {
  releaseNote: ReleaseNote
  isLatest?: boolean
}

/**
 * Release note detail component
 *
 * Displays the full content of a release note with formatted markdown,
 * version badge, date, and navigation controls.
 *
 * @param releaseNote - The release note to display
 * @param isLatest - Whether this is the latest release
 */
export function ReleaseNoteDetail({ releaseNote, isLatest = false }: ReleaseNoteDetailProps) {
  return (
    <div className="space-y-8">
      {/* Back Navigation */}
      <div className="flex items-center justify-between">
        <Link
          to="/release-notes"
          className={cn(buttonVariants({ variant: "ghost", size: "sm" }), "group flex items-center")}
        >
          <ArrowLeft className="mr-2 h-4 w-4 group-hover:-translate-x-1 transition-transform" />
          Back to all releases
        </Link>
      </div>

      {/* Header Section */}
      <div className="space-y-6">
        <div className="flex flex-wrap items-center gap-3">
          <Badge variant="outline" className="text-base px-4 py-1.5 font-mono">
            <Package className="h-4 w-4" />
            {releaseNote.version}
          </Badge>
          {isLatest && (
            <Badge variant="secondary" className="text-base px-4 py-1.5">
              <Sparkles className="h-4 w-4" />
              Latest Release
            </Badge>
          )}
          <div className="flex items-center gap-2 text-muted-foreground">
            <Calendar className="h-4 w-4" />
            <span className="text-sm">{format(new Date(releaseNote.date), 'MMMM dd, yyyy')}</span>
          </div>
        </div>

        <div>
          <h1 className="text-5xl font-bold tracking-tight bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text text-transparent">
            {releaseNote.title}
          </h1>
        </div>
      </div>

      <Separator className="my-8" />

      {/* Content Section */}
      <Card className="border-2">
        <CardContent className="p-8 md:p-12">
          <MarkdownContent content={releaseNote.content} />
        </CardContent>
      </Card>

      {/* Footer */}
      <div className="pt-8 border-t">
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Released on {format(new Date(releaseNote.date), 'MMMM dd, yyyy')}
          </p>
          <Link
            to="/release-notes"
            className={buttonVariants({ variant: "outline", size: "sm" })}
          >
            View all releases
          </Link>
        </div>
      </div>
    </div>
  )
}

interface ListItem {
  content: React.ReactNode
  nested?: React.ReactNode[]
}

/**
 * Renders markdown content with appropriate styling
 *
 * Converts markdown to HTML-like structure using React components.
 * Supports headers, lists, code blocks, images, and inline formatting.
 *
 * @param content - The markdown content to render
 */
function MarkdownContent({ content }: { content: string }) {
  const lines = content.split('\n')
  const elements: React.ReactElement[] = []
  let currentList: ListItem[] = []
  let currentCodeBlock: string[] = []
  let currentTable: string[] = []
  let inCodeBlock = false
  let inTable = false

  const flushList = () => {
    if (currentList.length > 0) {
      elements.push(
        <ul key={`list-${elements.length}`} className="space-y-2 my-6 ml-6">
          {currentList.map((item, i) => (
            <li key={i} className="flex items-start gap-3">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-primary mt-2.5 flex-shrink-0" />
              <div className="text-foreground/90 leading-relaxed flex-1">
                <span>{item.content}</span>
                {item.nested && item.nested.length > 0 && (
                  <ul className="space-y-2 mt-2 ml-6">
                    {item.nested.map((nestedItem, j) => (
                      <li key={j} className="flex items-start gap-3">
                        <span className="text-foreground/60 font-normal flex-shrink-0">-</span>
                        <span className="text-foreground/80 leading-relaxed flex-1">{nestedItem}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </li>
          ))}
        </ul>
      )
      currentList = []
    }
  }

  const flushCodeBlock = () => {
    if (currentCodeBlock.length > 0) {
      elements.push(
        <pre key={`code-${elements.length}`} className="bg-muted/50 border p-6 rounded-xl overflow-x-auto my-6">
          <code className="text-sm font-mono text-black dark:text-black">{currentCodeBlock.join('\n')}</code>
        </pre>
      )
      currentCodeBlock = []
    }
  }

  const flushTable = () => {
    if (currentTable.length > 2) {
      const headers = currentTable[0].split('|').filter((h) => h.trim())
      const rows = currentTable.slice(2).map((row) => row.split('|').filter((cell) => cell.trim()))

      elements.push(
        <div key={`table-${elements.length}`} className="my-8 overflow-x-auto">
          <table className="w-full border-collapse bg-card rounded-lg overflow-hidden">
            <thead>
              <tr className="bg-muted/50">
                {headers.map((header, i) => (
                  <th key={i} className="text-left p-4 font-semibold border-b">
                    {formatText(header.trim())}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr key={i} className="border-b last:border-0 hover:bg-muted/30 transition-colors">
                  {row.map((cell, j) => (
                    <td key={j} className="p-4 text-sm">
                      {formatText(cell.trim())}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )
      currentTable = []
    }
  }

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]

    // Code block
    if (line.startsWith('```')) {
      if (inCodeBlock) {
        flushCodeBlock()
        inCodeBlock = false
      } else {
        flushList()
        flushTable()
        inCodeBlock = true
      }
      continue
    }

    if (inCodeBlock) {
      currentCodeBlock.push(line)
      continue
    }

    // Table detection
    if (line.includes('|') && line.trim().startsWith('|')) {
      flushList()
      inTable = true
      currentTable.push(line)
      continue
    } else if (inTable && !line.includes('|')) {
      flushTable()
      inTable = false
    } else if (inTable) {
      currentTable.push(line)
      continue
    }

    // Headers with emoji icons
    if (line.startsWith('# ')) {
      flushList()
      continue // Skip main title as it's shown separately
    } else if (line.startsWith('## ')) {
      flushList()
      const text = line.replace(/^##\s+/, '')
      elements.push(
        <div key={`h2-${i}`} className="mt-12 mb-6 pb-4 border-b">
          <h2 className="text-3xl font-bold flex items-center gap-3">{formatText(text)}</h2>
        </div>
      )
    } else if (line.startsWith('### ')) {
      flushList()
      const text = line.replace(/^###\s+/, '')
      elements.push(
        <div key={`h3-${i}`} className="mt-10 mb-5">
          <h3 className="text-2xl font-semibold flex items-center gap-2">{formatText(text)}</h3>
        </div>
      )
    } else if (line.startsWith('#### ')) {
      flushList()
      const text = line.replace(/^####\s+/, '')
      elements.push(
        <h4 key={`h4-${i}`} className="text-lg font-semibold mt-6 mb-3 text-primary/90">
          {formatText(text)}
        </h4>
      )
    }
    // Lists - Nested bullets (4 spaces + - or *)
    else if (line.match(/^    [-*]\s+/)) {
      // Add to nested list of the last top-level item
      if (currentList.length > 0) {
        const lastItem = currentList[currentList.length - 1]
        if (!lastItem.nested) {
          lastItem.nested = []
        }
        lastItem.nested.push(formatText(line.replace(/^    [-*]\s+/, '')))
      }
    }
    // Lists - Top-level bullets
    else if (line.match(/^[-*]\s+/)) {
      currentList.push({
        content: formatText(line.replace(/^[-*]\s+/, '')),
        nested: []
      })
    }
    // Horizontal rule
    else if (line.trim().match(/^---+$/)) {
      flushList()
      elements.push(
        <Separator key={`hr-${i}`} className="my-10 opacity-100" style={{ height: '1px', backgroundColor: 'hsl(var(--border))' }} />
      )
    }
    // Images - ![alt text](image-path)
    else if (line.match(/^!\[.*?\]\(.*?\)/)) {
      flushList()
      const match = line.match(/^!\[(.*?)\]\((.*?)\)/)
      if (match) {
        const altText = match[1]
        const imagePath = match[2]
        // Convert relative path to absolute path
        const fullPath = imagePath.startsWith('/') ? imagePath : imagePath.replace(/^\.\//, '/')


        elements.push(
          <div key={`img-${i}`} className="my-8">
            <div className="relative w-full rounded-lg overflow-hidden border-2 bg-muted/30">
              <img src={fullPath} alt={altText} className="w-full h-auto" />
            </div>
          </div>
        )
      }
    }
    // Image captions (italic text after image)
    else if (line.match(/^\*[^*]+\*$/)) {
      const caption = line.replace(/^\*(.+)\*$/, '$1')
      elements.push(
        <p key={`caption-${i}`} className="text-sm text-muted-foreground text-center -mt-6 mb-6 italic">
          {caption}
        </p>
      )
    }
    // HTML comments (skip)
    else if (line.trim().startsWith('<!--') || line.trim().endsWith('-->')) {
      // Skip HTML comments
      continue
    }
    // Paragraph
    else if (line.trim().length > 0) {
      flushList()
      elements.push(
        <p key={`p-${i}`} className="text-foreground/90 my-4 leading-relaxed text-base">
          {formatText(line)}
        </p>
      )
    } else {
      flushList()
    }
  }

  flushList()
  flushCodeBlock()
  flushTable()

  return <div className="prose prose-neutral dark:prose-invert max-w-none">{elements}</div>
}

/**
 * Formats inline markdown syntax (bold, italic, links, code)
 *
 * Converts markdown formatting to React components with enhanced styling.
 * Handles bold (**text**), italic (*text*), links, and inline code.
 *
 * @param text - Text with markdown formatting
 * @returns Formatted text as React nodes
 */
function formatText(text: string | React.ReactNode): React.ReactNode {
  if (typeof text !== 'string') return text

  // Split by bold markers and process
  const parts = text.split(/(\*\*[^*]+\*\*)/g)

  return parts.map((part, i) => {
    // Bold text
    if (part.startsWith('**') && part.endsWith('**')) {
      return (
        <strong key={i} className="font-semibold text-foreground">
          {part.slice(2, -2)}
        </strong>
      )
    }
    return part
  })
}
